import React, { useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
} from "@mui/material";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import PostData from "../../services/PostData";
import SnackbarEx from "../Helpers/SnackbarEx";
import AuthenticatedAssetImage from "../Helpers/AuthenticatedAssetImage.jsx";
import {
    catalogPrimaryButtonSx,
    catalogQuietButtonSx,
    catalogSearchFieldSx,
} from "../Products/catalogGridStyles.js";

export default function ConfirmEbayRecord({ recordData, closeDialog }) {
    const [submitLoad, setSubmitLoad] = useState(false);
    const [successUpload, setSuccessUpload] = useState(false);
    const [openTestSnack, setOpenTestSnack] = useState({
        color: "",
        message: "",
        open: false,
    });

    const heroAsset = recordData?.fields?.["Hero Photo"]?.[0] || null;

    const [itemData, setItemData] = useState([
        { field: "Hero", data: recordData?.fields?.["Hero Photo"] ?? "", name: "hero", required: true },
        { field: "SKU", data: recordData?.sku ?? "", name: "sku", required: true },
        {
            field: "Brand",
            data: recordData?.fields?.["Brand API"]?.[0] ?? "",
            name: "brand",
            required: true,
        },
        {
            field: "Item Name",
            data: recordData?.fields?.["Item Name"] ?? "",
            name: "item_name",
            required: true,
        },
        {
            field: "UPC",
            data: recordData?.fields?.GTIN?.text ?? "",
            name: "upc",
            required: false,
        },
        {
            field: "Listing Field",
            data: recordData?.fields?.["Listing Field"] ?? "",
            name: "listing_field",
            required: true,
        },
        {
            field: "Listing Price",
            data: "",
            name: "listing_price",
            helper: "Price sent with the eBay offer",
            required: true,
        },
    ]);
    const [errors, setErrors] = useState({});

    const handleInputChanges = (e, fieldName) => {
        const { value } = e.target;
        setItemData((prevVals) =>
            prevVals.map((val) => (val.name === fieldName ? { ...val, data: value } : val))
        );
        if (value) {
            setErrors((prevErrors) => ({ ...prevErrors, [fieldName]: "" }));
        }
    };

    const handleSubmit = async () => {
        setSubmitLoad(true);
        const newErrors = {};
        let hasError = false;

        itemData.forEach((val) => {
            if (val.required && !val.data) {
                newErrors[val.name] = `${val.field} cannot be blank`;
                hasError = true;
            }
        });

        if (hasError) {
            setErrors(newErrors);
            setSubmitLoad(false);
            return;
        }

        const { error } = await PostData(itemData, "broylesllc/create-ebay-sku", null);
        if (error) {
            setSubmitLoad(false);
            setOpenTestSnack({
                color: "error",
                message: "An error has occurred.",
                open: true,
            });
            return;
        }
        setSuccessUpload(true);
        setTimeout(() => closeDialog("success"), 900);
    };

    return (
        <>
            <DialogTitle
                sx={{
                    fontWeight: 700,
                    letterSpacing: "-0.02em",
                    color: "#1c2430",
                    pb: 1,
                }}
            >
                Confirm eBay upload
            </DialogTitle>
            <DialogContent dividers sx={{ py: 2 }}>
                {heroAsset ? (
                    <Box sx={{ mb: 2 }}>
                        <AuthenticatedAssetImage
                            asset={heroAsset}
                            alt=""
                            sx={{
                                width: 64,
                                height: 64,
                                borderRadius: "8px",
                                border: "1px solid #e2e8f0",
                                backgroundColor: "#f8fafc",
                            }}
                        />
                    </Box>
                ) : null}
                <div className="row g-2 m-0">
                    {itemData
                        .filter((val) => val.field !== "Hero")
                        .map((val) => (
                            <div className="col-12" key={val.name}>
                                <TextField
                                    size="small"
                                    label={val.field}
                                    name={val.name}
                                    value={val.data}
                                    onChange={(e) => handleInputChanges(e, val.name)}
                                    fullWidth
                                    error={!!errors[val.name]}
                                    helperText={errors[val.name] ?? val?.helper}
                                    sx={catalogSearchFieldSx}
                                />
                            </div>
                        ))}
                </div>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <Button onClick={() => closeDialog()} sx={catalogQuietButtonSx}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disabled={submitLoad || successUpload}
                    onClick={handleSubmit}
                    startIcon={
                        successUpload ? (
                            <CheckOutlinedIcon fontSize="small" />
                        ) : submitLoad ? (
                            <CircularProgress size={14} color="inherit" />
                        ) : null
                    }
                    sx={{
                        ...catalogPrimaryButtonSx,
                        ...(successUpload
                            ? {
                                  backgroundColor: "#1fa971",
                                  "&:hover": { backgroundColor: "#1fa971" },
                              }
                            : {}),
                    }}
                >
                    {successUpload ? "Listed" : "Upload to eBay"}
                </Button>
            </DialogActions>
            {openTestSnack.open && (
                <SnackbarEx
                    color={openTestSnack.color}
                    message={openTestSnack.message}
                    open={openTestSnack.open}
                    close={() =>
                        setOpenTestSnack({ color: "", message: "", open: false })
                    }
                />
            )}
        </>
    );
}
